/**
 * Error utilities — consistent error handling across all service hooks.
 * (v4.8 Spec — Decision 6: Error Handling Contract)
 */

import type { ServiceError, ServiceErrorCode, ErrorSeverity } from '@/types/common';

/** Create a standardised ServiceError */
export function createServiceError(
  code: ServiceErrorCode,
  message: string,
  options?: {
    retry?: boolean;
    severity?: ErrorSeverity;
  }
): ServiceError {
  return {
    code,
    message,
    retry: options?.retry ?? false,
    severity: options?.severity ?? 'error',
  };
}

/** Convert a Supabase error to a ServiceError.
 *  Accepts Supabase PostgrestError and AuthError shapes —
 *  AuthError.code is `string | undefined` (not optional property),
 *  so we accept both via `string | undefined`. */

export function fromSupabaseError(error: {
  code?: string | undefined;
  message: string;
  details?: string;
}): ServiceError {
  const code = mapSupabaseErrorCode(error.code);
  return createServiceError(code, error.message, {
    retry: code === 'NETWORK_ERROR',
    severity: code === 'PERMISSION_DENIED' ? 'critical' : 'error',
  });
}

/** Map Supabase PGRST error codes to our ServiceErrorCode */
function mapSupabaseErrorCode(code: string | undefined): ServiceErrorCode {
  if (!code) return 'UNKNOWN_ERROR';

  switch (code) {
    case '42501': // insufficient_privilege
    case 'PGRST301': // JWT expired
      return 'PERMISSION_DENIED';
    case '23505': // unique_violation
      return 'CONFLICT';
    case 'PGRST116': // not found
      return 'NOT_FOUND';
    case '23503': // foreign_key_violation
      return 'VALIDATION_ERROR';
    case '429': // rate limited
      return 'RATE_LIMITED';
    default:
      if (code.startsWith('PGRST')) return 'SUPABASE_ERROR';
      return 'UNKNOWN_ERROR';
  }
}

/** Type guard to check if something is a ServiceError */
export function isServiceError(value: unknown): value is ServiceError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'message' in value &&
    'retry' in value &&
    'severity' in value
  );
}
